import React, { useMemo } from 'react';
import { Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { VisibilityTableState } from '@tanstack/table-core/src/features/ColumnVisibility';

import { TableType } from '../../../../../common/components/Table/model/enums/table-type.enum';
import { Table } from '../../../../../common/components/Table/Table';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { LanguagesConfig } from '../../../../../contexts/LocalizationContext/types';
import { useLocalizationContext } from '../../../../../contexts/LocalizationContext/useLocalizationContext.hook';
import { daysBetween, formatDate } from '../../../../../utils/dateHelper';
import { ScannerMode } from '../../../../RelevoAssignment/model/enums/scanner-mode.enum';
import { DepositRelevoTableMode } from '../../../model/enums/deposit-relevo-table-mode.enum';
import { isAnyItemExpired as checkItemsExpired } from '../../../utils/deposit.helper';

interface DepositRelevo {
  productName: string | null;
  category: {
    translations: LanguagesConfig;
  } | null;
  deposit?: string;
  expiresAt?: string | null;
  returnedAt?: string | null;
  error?: string;
  message?: string;
}

type DepositRelevoTableProps = {
  tableMode: DepositRelevoTableMode;
  scannerMode: ScannerMode;
  items: DepositRelevo[];
};
export const DepositRelevoTable = ({ items, tableMode, scannerMode }: DepositRelevoTableProps) => {
  const translations = useTranslations();
  const { language } = useLocalizationContext();
  const isAnyItemExpired = useMemo(() => checkItemsExpired(items), [items]);

  const columns = useMemo<ColumnDef<DepositRelevo>[]>(
    () => [
      {
        id: 'category',
        header: translations('item_category'),
        cell: ({ row }) => <>{row.original.category?.translations[language]}</>,
      },
      { accessorKey: 'productName', header: translations('item_name') },
      { accessorKey: 'message', header: translations('error') },
      { accessorKey: 'deposit', header: translations('deposit') },
      {
        id: 'expiresAt',
        header: translations('deposit_expiry_days'),
        cell: ({ row }) => {
          if (!row.original.expiresAt || !row.original.returnedAt) return null;
          const expiryDate = new Date(row.original.expiresAt);
          const returnDate = new Date(row.original.returnedAt);
          const isExpired = expiryDate < returnDate;
          const daysLate = isExpired ? daysBetween(expiryDate, returnDate) : null;
          if (daysLate) {
            return (
              <Text fontWeight={'bold'}>
                {formatDate(expiryDate)} ({daysLate} {translations('deposit_expiry_days_late_suffix')})
              </Text>
            );
          }
          return <Text>{formatDate(expiryDate)}</Text>;
        },
      },
    ],
    [language, translations],
  );

  const tableState: VisibilityTableState = useMemo(() => {
    return {
      columnVisibility: {
        category: true,
        productName: true,
        message: tableMode === DepositRelevoTableMode.ERROR,
        deposit: tableMode === DepositRelevoTableMode.SUCCESS,
        expiresAt: scannerMode === ScannerMode.DEPOSIT_RETURN && isAnyItemExpired,
      },
    };
  }, [tableMode, isAnyItemExpired, scannerMode]);

  return (
    <Table type={TableType.SIMPLE} variant="relevoSummary" data={items} columns={columns} tableState={tableState} />
  );
};
